import type { jsPDF as JsPdf } from 'jspdf';
import type { autoTable as AutoTable, CellHookData } from 'jspdf-autotable';
import regularFontUrl from '@/assets/font/pdf/Assistant-Regular.ttf?url';
import boldFontUrl from '@/assets/font/pdf/Assistant-Bold.ttf?url';

/*
 * HAT-1399: a board's rows as a PDF file the user downloads. Printing the page cannot stand
 * in for it: the table is virtualised, so a printout holds only the rows on screen.
 *
 * Hebrew: jsPDF places glyphs left to right and does no bidi of its own that can be trusted.
 * It ships a Unicode bidi engine (jsPDF.__bidiEngine__) and runs it over every text() call,
 * but that default pass assumes its input is already in visual order, and on a logical
 * Hebrew line it turns numbers and Latin words back to front ("123" prints as "321"). So each
 * line is reordered here, once, into the order it is drawn in, and the document's text() is
 * told that both its input and its output are visual, which makes the default pass a no-op.
 * Wrapping happens before the reorder, on the logical line, so a long name breaks at the
 * point a reader expects and its first words stay on the first line.
 *
 * Fonts: Assistant, the app's own font, as two static instances (wght 400 and 700) cut from
 * src/assets/font/Assistant-VariableFont_wght.ttf with fontTools' varLib.instancer. The
 * variable file cannot be embedded as is: PDF has no font variations, so a viewer draws its
 * default instance, which for Assistant is ExtraLight (wght 200) - too faint for a printout.
 */

export interface PdfColumn<T> {
  header: string;
  /** The cell's text. A '\n' starts a second line in the cell. */
  value: (row: T) => string;
  /** Width in mm. Columns without one share whatever is left of the page. */
  width?: number;
}

/** A run of rows under an optional heading, e.g. one of the board's groups. */
export interface PdfSection<T> {
  label?: string;
  rows: T[];
}

export interface PdfTableDocument<T> {
  /** Without the .pdf extension. */
  fileName: string;
  title: string;
  /** Lines under the title, on the start side: what the document covers. */
  details: string[];
  /** An image (the site's logo) on the end side of the title. Any format an <img> can show. */
  logoUrl?: string;
  columns: PdfColumn<T>[];
  sections: PdfSection<T>[];
  /** Shown in place of the rows when there are none. */
  emptyText: string;
  /** Repeated on every page's footer, next to the page number. */
  footerLabel: string;
  pageLabel: (page: number, total: number) => string;
  direction: 'rtl' | 'ltr';
}

type Rgb = [number, number, number];

// The client's design tokens (index.scss), the same ones the server's PDFs use (HatzdakaPdfStationery).
const BRAND: Rgb = [0x68, 0x1c, 0x3c];
const TEXT_DARK: Rgb = [0x2a, 0x2a, 0x2a];
const TEXT_GRAY: Rgb = [0x88, 0x88, 0x88];
const DIVIDER: Rgb = [0xe4, 0xe4, 0xe4];
const ROW_TINT: Rgb = [0xfe, 0xf7, 0xfa];
const SECTION_FILL: Rgb = [0xf4, 0xe8, 0xed];

const FONT = 'Assistant';
const MARGIN = { top: 14, side: 12, bottom: 16 };
const BIDI_MARKS = /[\u200e\u200f\u061c\u202a-\u202e\u2066-\u2069]/g;
const HEBREW = /[\u0590-\u05ff]/;

interface BidiEngine {
  doBidiReorder: (text: string) => string;
}
type BidiEngineConstructor = new (options: Record<string, boolean>) => BidiEngine;
type JsPdfConstructor = typeof import('jspdf').jsPDF;

export interface PdfFonts {
  regular: string;
  bold: string;
}

/** A PNG ready for jsPDF, with its height / width so it can be scaled to a chosen width. */
export interface PdfImage {
  dataUrl: string;
  aspect: number;
}

export interface PdfAssets {
  fonts: PdfFonts;
  logo?: PdfImage;
}

const LOGO_WIDTH_MM = 32;

/**
 * The order a logical line's characters are drawn in, left to right. A line with Hebrew in it
 * is laid out right to left, with its numbers and Latin words kept in reading order; any other
 * line is left as it is. Bidi control marks (which Intl puts around currency signs) are dropped,
 * since the reorder has already done their job.
 */
export function toVisualLine(line: string, JsPdf: JsPdfConstructor): string {
  const clean = line.replace(BIDI_MARKS, '');
  if (!HEBREW.test(clean)) return clean;
  const Engine = (JsPdf as unknown as { __bidiEngine__: BidiEngineConstructor }).__bidiEngine__;
  const engine = new Engine({ isInputVisual: false, isOutputVisual: true, isInputRtl: true, isOutputRtl: false, isSymmetricSwapping: true });
  return engine.doBidiReorder(clean);
}

/** Builds the document. Separate from {@link exportRowsToPdf} so it can run without a browser. */
export function renderPdfTable<T>(JsPdf: JsPdfConstructor, autoTable: typeof AutoTable, { fonts, logo }: PdfAssets, spec: PdfTableDocument<T>): JsPdf {
  const doc = new JsPdf({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });
  doc.addFileToVFS('Assistant-Regular.ttf', fonts.regular);
  doc.addFont('Assistant-Regular.ttf', FONT, 'normal');
  doc.addFileToVFS('Assistant-Bold.ttf', fonts.bold);
  doc.addFont('Assistant-Bold.ttf', FONT, 'bold');

  // Every line reaches text() already in visual order - see the note at the top of the file.
  const drawText = doc.text.bind(doc);
  doc.text = ((text: string | string[], x: number, y: number, options?: Record<string, unknown>, transform?: unknown) =>
    drawText(text, x, y, { ...options, isInputVisual: true, isOutputVisual: true }, transform)) as typeof doc.text;
  const visual = (line: string) => toVisualLine(line, JsPdf);

  const rtl = spec.direction === 'rtl';
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const startX = rtl ? pageWidth - MARGIN.side : MARGIN.side;
  const endX = rtl ? MARGIN.side : pageWidth - MARGIN.side;
  const startAlign = rtl ? 'right' : 'left';
  const endAlign = rtl ? 'left' : 'right';

  let y = MARGIN.top + 2;
  doc.setFont(FONT, 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...BRAND);
  doc.text(visual(spec.title), startX, y, { align: startAlign });
  let logoBottom = 0;
  if (logo) {
    // Its top level with the top of the title, as the app header pairs the logo with the page name.
    const height = LOGO_WIDTH_MM * logo.aspect;
    const top = MARGIN.top - 4;
    doc.addImage(logo.dataUrl, 'PNG', rtl ? endX : endX - LOGO_WIDTH_MM, top, LOGO_WIDTH_MM, height);
    logoBottom = top + height;
  }
  y += 7;
  doc.setFont(FONT, 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...TEXT_DARK);
  for (const line of spec.details) {
    doc.text(visual(line), startX, y, { align: startAlign });
    y += 5;
  }
  y = Math.max(y, logoBottom + 3);
  doc.setDrawColor(...DIVIDER);
  doc.setLineWidth(0.3);
  doc.line(MARGIN.side, y, pageWidth - MARGIN.side, y);

  // Columns are laid out left to right, so a right-to-left table lists them from its end.
  const columns = rtl ? [...spec.columns].reverse() : spec.columns;
  const halign = startAlign;
  const fullWidth = (content: string, styles: Record<string, unknown>) => [{ content, colSpan: columns.length, styles }];

  const sections = spec.sections.filter((section) => section.rows.length > 0);
  const body = sections.length
    ? sections.flatMap((section) => [
        ...(section.label ? [fullWidth(`${section.label} (${section.rows.length})`, { fontStyle: 'bold', fillColor: SECTION_FILL, textColor: BRAND })] : []),
        ...section.rows.map((row) => columns.map((column) => column.value(row))),
      ])
    : [fullWidth(spec.emptyText, { textColor: TEXT_GRAY, halign: 'center' })];

  const converted = new WeakSet<object>();
  autoTable(doc, {
    startY: y + 4,
    margin: { top: MARGIN.top, bottom: MARGIN.bottom, left: MARGIN.side, right: MARGIN.side },
    head: [columns.map((column) => column.header)],
    body,
    showHead: 'everyPage',
    rowPageBreak: 'avoid',
    theme: 'grid',
    styles: {
      font: FONT,
      fontStyle: 'normal',
      fontSize: 8.5,
      textColor: TEXT_DARK,
      lineColor: DIVIDER,
      lineWidth: 0.2,
      cellPadding: { top: 1.6, bottom: 1.6, left: 1.8, right: 1.8 },
      halign,
      valign: 'middle',
      overflow: 'linebreak',
    },
    headStyles: { fontStyle: 'bold', fillColor: BRAND, textColor: [0xff, 0xff, 0xff], halign },
    alternateRowStyles: { fillColor: ROW_TINT },
    columnStyles: Object.fromEntries(columns.map((column, index) => [index, column.width ? { cellWidth: column.width } : { cellWidth: 'auto' }])),
    willDrawCell: (data: CellHookData) => {
      if (converted.has(data.cell)) return;
      converted.add(data.cell);
      data.cell.text = data.cell.text.map(visual);
    },
  });

  const pages = doc.getNumberOfPages();
  for (let page = 1; page <= pages; page++) {
    doc.setPage(page);
    doc.setFont(FONT, 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...TEXT_GRAY);
    const footerY = pageHeight - MARGIN.bottom / 2;
    doc.text(visual(spec.footerLabel), startX, footerY, { align: startAlign });
    doc.text(visual(spec.pageLabel(page, pages)), endX, footerY, { align: endAlign });
  }

  return doc;
}

let fontsPromise: Promise<PdfFonts> | null = null;

async function fetchBase64(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Could not load ${url}: HTTP ${response.status}`);
  const bytes = new Uint8Array(await response.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}

function loadFonts(): Promise<PdfFonts> {
  fontsPromise ??= Promise.all([fetchBase64(regularFontUrl), fetchBase64(boldFontUrl)])
    .then(([regular, bold]) => ({ regular, bold }))
    .catch((error) => {
      // A failed load is not cached, so the next click tries again.
      fontsPromise = null;
      throw error;
    });
  return fontsPromise;
}

const logos = new Map<string, Promise<PdfImage>>();

/**
 * jsPDF takes no SVG, so the logo is drawn onto a canvas and handed over as a PNG - at eight
 * times its CSS size, which keeps its edges sharp in print (about 650 dpi at the width it is placed).
 */
function loadLogo(url: string): Promise<PdfImage> {
  let logo = logos.get(url);
  if (!logo) {
    logo = (async () => {
      const image = new Image();
      image.src = url;
      await image.decode();
      const canvas = document.createElement('canvas');
      canvas.width = image.naturalWidth * 8;
      canvas.height = image.naturalHeight * 8;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('No 2d canvas context');
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      return { dataUrl: canvas.toDataURL('image/png'), aspect: image.naturalHeight / image.naturalWidth };
    })();
    logos.set(url, logo);
    logo.catch(() => logos.delete(url));
  }
  return logo;
}

/** Builds the document and hands it to the browser as a download. Loads jsPDF only when asked. */
export async function exportRowsToPdf<T>(spec: PdfTableDocument<T>): Promise<void> {
  // A logo that fails to load costs the document its logo, not the document.
  const logo = spec.logoUrl
    ? loadLogo(spec.logoUrl).catch((error) => {
        console.warn('PDF logo could not be loaded:', error);
        return undefined;
      })
    : undefined;
  const [{ jsPDF }, { autoTable }, fonts, logoImage] = await Promise.all([import('jspdf'), import('jspdf-autotable'), loadFonts(), logo]);
  renderPdfTable(jsPDF, autoTable, { fonts, logo: logoImage }, spec).save(`${spec.fileName}.pdf`);
}
